import { Box3, Group, Material, Mesh, Plane, Vector3 } from 'three';
import { EventEmitter } from '../core/EventEmitter';

export interface SectionState {
  active: boolean;
  offset: number;
  range: number;
  flipped: boolean;
}

export class SectionController {
  readonly changed = new EventEmitter<SectionState>();
  private readonly plane = new Plane();
  private origin = new Vector3();
  private normal = new Vector3(0, 1, 0);
  private offset = 0;
  private range = 10;
  private active = false;
  private flipped = false;

  constructor(private readonly modelsRoot: Group) {}

  start(point: Vector3, normal: Vector3): void {
    this.origin.copy(point);
    this.normal.copy(normal).normalize();
    this.offset = 0;
    this.flipped = false;
    const box = new Box3().setFromObject(this.modelsRoot);
    this.range = box.isEmpty() ? 10 : Math.max(box.getSize(new Vector3()).length() * 0.65, 1);
    this.active = true;
    this.updatePlane();
    this.applyPlane();
    this.emit();
  }

  setOffset(offset: number): void {
    this.offset = Math.max(-this.range, Math.min(offset, this.range));
    this.updatePlane();
    this.emit();
  }

  get position(): Vector3 | null {
    return this.active ? this.origin.clone().addScaledVector(this.normal, this.offset) : null;
  }

  get direction(): Vector3 {
    return this.normal.clone();
  }

  get currentOffset(): number {
    return this.offset;
  }

  get currentRange(): number {
    return this.range;
  }

  flip(): void {
    const position = this.position;
    this.flipped = !this.flipped;
    this.normal.negate();
    if (position) this.origin.copy(position);
    this.offset = 0;
    this.updatePlane();
    this.emit();
  }

  clear(): void {
    this.active = false;
    this.modelsRoot.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      for (const material of this.materialsOf(object)) material.clippingPlanes = null;
    });
    this.emit();
  }

  synchronize(): void {
    if (this.active) this.applyPlane();
  }

  private updatePlane(): void {
    const point = this.origin.clone().addScaledVector(this.normal, this.offset);
    this.plane.setFromNormalAndCoplanarPoint(this.normal, point);
  }

  private applyPlane(): void {
    this.modelsRoot.traverse((object) => {
      if (!(object instanceof Mesh)) return;
      for (const material of this.materialsOf(object)) material.clippingPlanes = [this.plane];
    });
  }

  private materialsOf(mesh: Mesh): Material[] {
    return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  }

  private emit(): void {
    this.changed.emit({ active: this.active, offset: this.offset, range: this.range, flipped: this.flipped });
  }
}

